import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Button, IconButton, TextField, FormControlLabel,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Dialog, DialogTitle, DialogContent, DialogActions, Chip, CircularProgress,
  Alert, Tooltip, Checkbox, Collapse, InputAdornment, MenuItem, Select,
  FormControl, InputLabel
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  LocationOn as LocationIcon,
  DragIndicator as DragIcon,
} from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { pricingAPI } from '../../services/apiService';

const EMPTY_ZONE = {
  name: '',
  sortOrder: 100,
  normalizeTo: '',
};

// ---- Sortable Row Component ----
const SortableRow = ({ zone, children, onEdit, onDelete }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: zone.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: isDragging ? 'grabbing' : 'default',
  };
  return (
    <TableRow ref={setNodeRef} style={style} hover>
      <TableCell sx={{ width: 40, pl: 1 }}>
        <IconButton size="small" {...attributes} {...listeners} sx={{ cursor: 'grab', color: 'text.disabled' }}>
          <DragIcon fontSize="small" />
        </IconButton>
      </TableCell>
      {children}
    </TableRow>
  );
};

const ZonesManager = () => {
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null = create, object = edit
  const [form, setForm] = useState(EMPTY_ZONE);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [reordering, setReordering] = useState(false);

  const sensors = useSensors(useSensor(PointerSensor));

  // Zip code management
  const [selectedZipCodes, setSelectedZipCodes] = useState([]); // [{zip, city, state}]
  const [keywords, setKeywords] = useState(''); // comma-separated, for airports

  // Search state
  const [zipSearch, setZipSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]); // CityGroup[] from server
  const [searchLoading, setSearchLoading] = useState(false);
  const [expandedCities, setExpandedCities] = useState(new Set()); // track which city groups are expanded

  const searchDebounceRef = useRef(null);

  const fetchZones = useCallback(async () => {
    try {
      setLoading(true);
      const data = await pricingAPI.getZones();
      setZones(data);
      setError(null);
    } catch (err) {
      setError('Failed to load zones: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchZones(); }, [fetchZones]);

  // ---- Search Logic ----

  const handleSearchChange = (value) => {
    setZipSearch(value);

    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);

    if (value.trim().length < 2) {
      setSearchResults([]);
      return;
    }

    searchDebounceRef.current = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const results = await pricingAPI.searchZipCodes(value.trim());
        setSearchResults(results);
        // Auto-expand all city groups
        setExpandedCities(new Set(results.map(g => `${g.city}-${g.state}`)));
      } catch (err) {
        console.error('Zip code search failed:', err);
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 300);
  };

  // Toggle all zip codes in a city group
  const toggleCity = (group) => {
    const allSelected = group.zipCodes.every(z => selectedZipCodes.some(s => s.zip === z));
    if (allSelected) {
      // Remove all zips from this city
      setSelectedZipCodes(prev => prev.filter(s => !group.zipCodes.includes(s.zip)));
    } else {
      // Add missing zips from this city
      const toAdd = group.zipCodes
        .filter(z => !selectedZipCodes.some(s => s.zip === z))
        .map(z => ({ zip: z, city: group.city, state: group.state }));
      setSelectedZipCodes(prev => [...prev, ...toAdd]);
    }
  };

  // Toggle a single zip code
  const toggleZip = (zip, city, state) => {
    const exists = selectedZipCodes.some(s => s.zip === zip);
    if (exists) {
      setSelectedZipCodes(prev => prev.filter(s => s.zip !== zip));
    } else {
      setSelectedZipCodes(prev => [...prev, { zip, city, state }]);
    }
  };

  // Remove a zip from selected (chip delete)
  const removeZipCode = (zip) => {
    setSelectedZipCodes(prev => prev.filter(s => s.zip !== zip));
  };

  // Toggle city group expand/collapse
  const toggleExpanded = (key) => {
    setExpandedCities(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  // ---- Dialog Open/Close ----

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_ZONE);
    setSelectedZipCodes([]);
    setKeywords('');
    setZipSearch('');
    setSearchResults([]);
    setDialogOpen(true);
  };

  const openEdit = async (zone) => {
    setEditing(zone);
    setForm({
      name: zone.name,
      sortOrder: zone.sortOrder,
      normalizeTo: zone.normalizeTo || '',
    });

    // Parse matchRules
    let zips = [];
    let kw = [];
    try {
      const rules = JSON.parse(zone.matchRules);
      zips = rules.zipCodes || [];
      // Legacy format support
      if (rules.zipPatterns) {
        rules.zipPatterns.forEach(p => {
          if (/^\d{5}$/.test(p) && !zips.includes(p)) zips.push(p);
        });
      }
      kw = rules.keywords || [];
    } catch { /* ignore parse errors */ }

    setKeywords(kw.join(', '));
    setZipSearch('');
    setSearchResults([]);

    // Resolve city labels for existing zip codes via server
    if (zips.length > 0) {
      try {
        const labels = await pricingAPI.resolveZipCodes(zips);
        setSelectedZipCodes(zips.map(z => {
          const label = labels[z] || '';
          const parts = label.split(', ');
          return { zip: z, city: parts[0] || '', state: parts[1] || '' };
        }));
      } catch {
        // Fallback — no labels
        setSelectedZipCodes(zips.map(z => ({ zip: z, city: '', state: '' })));
      }
    } else {
      setSelectedZipCodes([]);
    }

    setDialogOpen(true);
  };

  // Build matchRules JSON from current selections
  const buildMatchRules = () => {
    const rules = {};
    const zips = selectedZipCodes.map(z => z.zip);
    if (zips.length) rules.zipCodes = zips;
    const kw = keywords.split(',').map(s => s.trim()).filter(Boolean);
    if (kw.length) rules.keywords = kw;
    return JSON.stringify(rules);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const matchRules = buildMatchRules();
      const payload = {
        name: form.name.toUpperCase().trim(),
        matchRules,
        sortOrder: parseInt(form.sortOrder) || 100,
        normalizeTo: form.normalizeTo?.trim() || null,
      };

      if (editing) {
        await pricingAPI.updateZone(editing.id, { ...payload, id: editing.id });
      } else {
        await pricingAPI.createZone(payload);
      }
      setDialogOpen(false);
      await fetchZones();
    } catch (err) {
      setError('Save failed: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleDragEnd = async ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIndex = zones.findIndex(z => z.id === active.id);
    const newIndex = zones.findIndex(z => z.id === over.id);
    const reordered = arrayMove(zones, oldIndex, newIndex).map((z, i) => ({ ...z, sortOrder: i + 1 }));
    setZones(reordered);
    try {
      setReordering(true);
      await pricingAPI.reorderZones(reordered.map((z, i) => ({ id: z.id, sortOrder: i + 1 })));
    } catch (err) {
      setError('Reorder failed: ' + (err.response?.data?.error || err.message));
      await fetchZones(); // revert
    } finally {
      setReordering(false);
    }
  };

  const handleDelete = async (zone) => {
    try {
      await pricingAPI.deleteZone(zone.id);
      setDeleteConfirm(null);
      await fetchZones();
    } catch (err) {
      setError('Delete failed: ' + (err.response?.data?.error || err.message));
      setDeleteConfirm(null);
    }
  };

  if (loading) {
    return <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>;
  }

  // Group selected zips by city for chip display
  const selectedByCity = selectedZipCodes.reduce((acc, z) => {
    const key = z.city ? `${z.city}, ${z.state}` : 'Unknown';
    if (!acc[key]) acc[key] = [];
    acc[key].push(z.zip);
    return acc;
  }, {});

  return (
    <Box>
      {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Pricing Zones ({zones.length})</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>Add Zone</Button>
      </Box>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 40 }} />
                <TableCell><strong>Order</strong></TableCell>
                <TableCell><strong>Name</strong></TableCell>
                <TableCell><strong>Zip Codes</strong></TableCell>
                <TableCell><strong>Keywords</strong></TableCell>
                <TableCell><strong>Normalizes To</strong></TableCell>
                <TableCell align="right"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <SortableContext items={zones.map(z => z.id)} strategy={verticalListSortingStrategy}>
                {zones.map((zone) => {
                  let rules = {};
                  try { rules = JSON.parse(zone.matchRules); } catch { }
                  const allZips = [...(rules.zipCodes || []), ...(rules.zipPatterns || []).filter(p => /^\d{5}$/.test(p))];
                  const uniqueZips = [...new Set(allZips)];
                  return (
                    <SortableRow key={zone.id} zone={zone}>
                      <TableCell>{zone.sortOrder}</TableCell>
                      <TableCell><strong>{zone.name}</strong></TableCell>
                      <TableCell>
                        <Box display="flex" gap={0.5} flexWrap="wrap">
                          {uniqueZips.slice(0, 5).map((z, i) => (
                            <Chip key={i} label={z} size="small" variant="outlined" color="primary" />
                          ))}
                          {uniqueZips.length > 5 && (
                            <Chip label={`+${uniqueZips.length - 5} more`} size="small" color="primary" />
                          )}
                          {uniqueZips.length === 0 && (rules.zipPatterns || []).length > 0 && (
                            <Tooltip title="Legacy regex patterns - edit zone to convert">
                              <Chip label="Legacy patterns" size="small" color="warning" />
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" gap={0.5} flexWrap="wrap">
                          {(rules.keywords || []).slice(0, 3).map((k, i) => (
                            <Chip key={i} label={k} size="small" variant="outlined" color="secondary" />
                          ))}
                        </Box>
                      </TableCell>
                      <TableCell>{zone.normalizeTo || '—'}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => openEdit(zone)}><EditIcon fontSize="small" /></IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error" onClick={() => setDeleteConfirm(zone)}><DeleteIcon fontSize="small" /></IconButton>
                        </Tooltip>
                      </TableCell>
                    </SortableRow>
                  );
                })}
              </SortableContext>
            </TableBody>
          </Table>
        </TableContainer>
      </DndContext>
      {reordering && <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>Saving order...</Typography>}

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editing ? `Edit Zone: ${editing.name}` : 'Add New Zone'}</DialogTitle>
        <DialogContent dividers>
          <Box display="flex" flexDirection="column" gap={2} pt={1}>
            <Box display="flex" gap={2}>
              <TextField
                label="Zone Name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                fullWidth
                required
                placeholder="e.g. LAKEWOOD"
                helperText="Uppercase identifier"
              />
              <TextField
                label="Sort Order"
                value={form.sortOrder}
                onChange={(e) => setForm({ ...form, sortOrder: e.target.value })}
                type="number"
                sx={{ width: 130 }}
                helperText="Lower = first"
              />
            </Box>

            {/* Zip Code Search & Selection */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Zip Codes ({selectedZipCodes.length} selected)
              </Typography>

              {/* Selected zip codes grouped by city */}
              {selectedZipCodes.length > 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    gap: 0.5,
                    flexWrap: 'wrap',
                    mb: 1.5,
                    p: 1.5,
                    bgcolor: 'action.hover',
                    borderRadius: 1,
                    maxHeight: 150,
                    overflow: 'auto',
                  }}
                >
                  {Object.entries(selectedByCity).map(([cityLabel, zips]) => (
                    <React.Fragment key={cityLabel}>
                      {selectedZipCodes.length > 3 && cityLabel !== 'Unknown' && (
                        <Chip
                          label={cityLabel}
                          size="small"
                          sx={{ fontWeight: 600, bgcolor: 'transparent', fontSize: '0.7rem' }}
                          icon={<LocationIcon sx={{ fontSize: 14 }} />}
                        />
                      )}
                      {zips.map(zip => (
                        <Chip
                          key={zip}
                          label={zip}
                          onDelete={() => removeZipCode(zip)}
                          color="primary"
                          variant="outlined"
                          size="small"
                        />
                      ))}
                    </React.Fragment>
                  ))}
                </Box>
              )}

              {/* Search box */}
              <TextField
                fullWidth
                size="small"
                placeholder="Search by zip code (e.g. 08701) or city name (e.g. Lakewood)..."
                value={zipSearch}
                onChange={(e) => handleSearchChange(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: searchLoading ? <CircularProgress size={18} /> : null
                }}
                helperText="Search for a zip code or city — nearby cities with all their zip codes will appear below"
              />

              {/* Search Results - Grouped by city */}
              {searchResults.length > 0 && (
                <Paper
                  variant="outlined"
                  sx={{ maxHeight: 350, overflow: 'auto', mt: 1 }}
                >
                  {searchResults.map((group) => {
                    const key = `${group.city}-${group.state}`;
                    const isExpanded = expandedCities.has(key);
                    const allSelected = group.zipCodes.every(z => selectedZipCodes.some(s => s.zip === z));
                    const someSelected = group.zipCodes.some(z => selectedZipCodes.some(s => s.zip === z));

                    return (
                      <Box key={key}>
                        {/* City header row */}
                        <Box
                          display="flex"
                          alignItems="center"
                          sx={{
                            px: 1,
                            py: 0.5,
                            bgcolor: someSelected ? 'primary.50' : 'grey.50',
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            cursor: 'pointer',
                            '&:hover': { bgcolor: 'action.hover' },
                          }}
                          onClick={() => toggleExpanded(key)}
                        >
                          <Checkbox
                            size="small"
                            checked={allSelected}
                            indeterminate={someSelected && !allSelected}
                            onClick={(e) => { e.stopPropagation(); toggleCity(group); }}
                            sx={{ mr: 0.5 }}
                          />
                          <LocationIcon fontSize="small" sx={{ mr: 0.5, color: 'primary.main' }} />
                          <Typography variant="subtitle2" sx={{ flexGrow: 1, fontWeight: 600 }}>
                            {group.city}, {group.state}
                            <Typography
                              component="span"
                              variant="caption"
                              sx={{ ml: 1, color: 'text.secondary', fontWeight: 400 }}
                            >
                              {group.zipCodes.length} zip{group.zipCodes.length !== 1 ? 's' : ''}
                              {group.distance > 0 ? ` · ${group.distance} mi` : ''}
                            </Typography>
                          </Typography>
                          <IconButton size="small">
                            {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                          </IconButton>
                        </Box>

                        {/* Individual zip codes */}
                        <Collapse in={isExpanded}>
                          <Box
                            display="flex"
                            flexWrap="wrap"
                            sx={{ px: 2, py: 0.5, pl: 6 }}
                          >
                            {group.zipCodes.map(zip => {
                              const isSelected = selectedZipCodes.some(s => s.zip === zip);
                              return (
                                <FormControlLabel
                                  key={zip}
                                  control={
                                    <Checkbox
                                      size="small"
                                      checked={isSelected}
                                      onChange={() => toggleZip(zip, group.city, group.state)}
                                    />
                                  }
                                  label={
                                    <Typography variant="body2" sx={{ fontWeight: isSelected ? 600 : 400 }}>
                                      {zip}
                                    </Typography>
                                  }
                                  sx={{ minWidth: 110, mr: 1 }}
                                />
                              );
                            })}
                          </Box>
                        </Collapse>
                      </Box>
                    );
                  })}
                </Paper>
              )}

              {/* "No results" message */}
              {zipSearch.trim().length >= 2 && !searchLoading && searchResults.length === 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  No zip codes found for &quot;{zipSearch}&quot;. Try a different city name or zip code.
                </Typography>
              )}
            </Box>

            {/* Keywords (for airports) */}
            <TextField
              label="Keywords (for airports only)"
              value={keywords}
              onChange={(e) => setKeywords(e.target.value)}
              fullWidth
              placeholder="JFK, John F. Kennedy, Kennedy Airport"
              helperText="Comma-separated. Used for airport name matching (e.g. 'JFK', 'LaGuardia'). Leave empty for non-airport zones."
            />

            <FormControl fullWidth>
              <InputLabel>Normalizes To</InputLabel>
              <Select
                value={form.normalizeTo}
                label="Normalizes To"
                onChange={(e) => setForm({ ...form, normalizeTo: e.target.value })}
              >
                <MenuItem value=""><em>None</em></MenuItem>
                {zones
                  .filter(z => !editing || z.id !== editing.id)
                  .map(z => (
                    <MenuItem key={z.id} value={z.name}>{z.name}</MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} startIcon={<CancelIcon />}>Cancel</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={saving || !form.name.trim() || (selectedZipCodes.length === 0 && !keywords.trim())}
          >
            {saving ? 'Saving...' : (editing ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)}>
        <DialogTitle>Delete Zone?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{deleteConfirm?.name}</strong>?
            This will fail if any route prices reference this zone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
          <Button onClick={() => handleDelete(deleteConfirm)} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ZonesManager;
